import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NavigationGridItem from "@midland-industries/Components/NavigationGridItem";
import loadNewProducts from "@midland-industries/Store/Data/NewProducts/Handlers/LoadNewProducts";
import { CustomNewProductsState } from "@midland-industries/Store/Data/NewProducts/NewProductsReducer";
import { NewProductCategoryModel, NewProductModel } from "@midland-industries/Types/NewProductModels";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showMoreTitle = "showMoreTitle",
    itemsToReturn = "itemsToReturn",
    showType = "showType",
    showAsCategory = "showAsCategory",
    newProductsAttributeId = "newProductsAttributeId"
}

interface OwnProps {
    fields: {
        [fields.title]: string;
        [fields.showMoreTitle]: string;
        [fields.itemsToReturn]: number;
        [fields.showType]: "categories" | "products";
        [fields.showAsCategory]: boolean;
        [fields.newProductsAttributeId]: string;

    };
}

const mapStateToProps = (state: CustomNewProductsState) => {
    const newProductState = state.xcentiumNewProducts;
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    return {
        newProductState,
        location,
        category: getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        ).value,
    };
};

const mapDispatchToProps = {
    loadNewProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NewProductLinkListStyles {
    container?: GridContainerProps;
    headerGridItem?: GridItemProps;
    headerWrapper?: InjectableCss;
    header?: TypographyPresentationProps;
    linkContainer?: GridContainerProps;
    linkWrapper?: InjectableCss;
    imageGridItem?: GridItemProps;
    imageClickable?: ClickablePresentationProps;
    img?: ImgProps;
    linkGridItem?: GridItemProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    linkTypography?: TypographyPresentationProps;
    footerGridItem?: GridItemProps;
    footerLink?: LinkPresentationProps;
    footer?: TypographyPresentationProps;
}

export const newProductLinkListStyles: NewProductLinkListStyles = {
    container: {
        gap: 10,
    },
    headerGridItem: {
        width: 12,
        css: css``,
    },
    headerWrapper: {
        css: css`
            width: 100%;
            padding: 10px 5px 10px 0;
            display: flex;
            justify-content: center;
        `,
    },
    header: {
        css: css`
            font-weight: 700;
            font-size: 18px;
        `,
    },
    linkContainer: {
        gap: 0,
    },
    linkWrapper: {
        css: css`
            width: 100%;
            padding: 5px;
            background-color: ${getColor("primary.contrast")};
            border-bottom: 1px solid ${getColor("secondary.main")};
        `,
    },
    imageGridItem: {
        width: 4,
    },
    linkGridItem: {
        width: 8,
        css: css`
            justify-content: center;
            align-items: stretch;
        `,
    },
    imageClickable: {
        css: css`
            width: 100%;
            max-width: 120px;
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    subCategoryNameLink: {
        css: css`
            margin: 0 5px 0 10px;
            width: 100%;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    footerGridItem: {
        width: 12,
    },
    footerLink: {
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    footer: {
        css: css`
            width: 100%;
            color: ${getColor("primary.contrast")};
        `,
    },
};

const styles = newProductLinkListStyles;

const NewProductLinkList: FC<Props> = props => {
    const { fields, newProductState, location, category, loadNewProducts } = props;
    const { title, showMoreTitle, itemsToReturn, showType, showAsCategory, newProductsAttributeId } = fields;

    const [cacheIndex, setCacheIndex] = useState(-1);

    const newProductsUrl = "/catalog/new-products";

    useEffect(() => {
        const existingIndex = newProductState.requestCache.findIndex(
            c =>
                c.params.categoryId === category?.id &&
                c.params.pageSize === itemsToReturn &&
                c.params.returnCategories === (showType === "categories") &&
                c.params.returnProductDtos === false,
        );
        if (existingIndex > -1) {
            if (existingIndex !== cacheIndex) {
                setCacheIndex(existingIndex);
            }
        }
    }, [newProductState]);

    useEffect(() => {
        if (location.pathname.indexOf("/catalog/") > -1 && category) {
            const existingIndex = newProductState.requestCache.findIndex(
                c =>
                    c.params.categoryId === category?.id &&
                    c.params.pageSize === itemsToReturn &&
                    c.params.returnCategories === (showType === "categories") &&
                    c.params.returnProductDtos === false,
            );
            if (existingIndex > -1) {
                setCacheIndex(existingIndex);
            } else {
                loadNewProducts({
                    returnCategories: showType === "categories",
                    returnProductDtos: false,
                    categoryId: category.id,
                    pageSize: itemsToReturn,
                });
            }
        }
        if (location.pathname.indexOf("/catalog/") < 0) {
            const existingIndex = newProductState.requestCache.findIndex(
                c =>
                    c.params.categoryId === category?.id &&
                    c.params.pageSize === itemsToReturn &&
                    c.params.returnCategories === (showType === "categories") &&
                    c.params.returnProductDtos === false,
            );
            if (existingIndex > -1) {
                setCacheIndex(existingIndex);
            } else {
                loadNewProducts({
                    returnCategories: showType === "categories",
                    returnProductDtos: false,
                    categoryId: undefined,
                    pageSize: itemsToReturn,
                });
            }
        }
    }, [category]);

    if (cacheIndex < 0) {
        return null;
    }
    if (
        newProductState.requestCache[cacheIndex]?.results.newProductCategories.length === 0 &&
        newProductState.requestCache[cacheIndex]?.results.newProducts.length === 0
    ) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.headerGridItem}>
                <StyledWrapper {...styles.headerWrapper}>
                    <Typography {...styles.header}>{title}</Typography>
                </StyledWrapper>
            </GridItem>
            {cacheIndex > -1 &&
                newProductState.requestCache[cacheIndex].newProductsLoaded &&
                newProductState.requestCache[cacheIndex].results.newProductCategories.map(
                    (category: NewProductCategoryModel, index) => (
                        showAsCategory ? (
                            <NavigationGridItem
                                key={`navigationGridItem_${index}`}
                                pageUrl={`${category.url}?attributeValueIds=${newProductsAttributeId}`}
                                imageUrl={category.imageUrl ?? ""}
                                imageAltText={category.imageAltText}
                                pageId={category.id ?? ""}
                                titleText={category.name ?? ""}
                                maxItemsPerRow={6}
                            />
                        ) : (
                            <GridItem
                            key={`gridItem_${index}`}
                            {...styles.subCategoryNameLinkItem}
                            data-key-selector="subCategoryNameLinkItem"
                        >
                            <StyledWrapper {...styles.linkWrapper} data-key-selector="linkWrapper">
                                <GridContainer {...styles.linkContainer} data-key-selector="linkContainer">
                                    <GridItem {...styles.imageGridItem} data-key-selector="imageGridItem">
                                        <Clickable {...styles.imageClickable} href={category?.url}>
                                            <Img
                                                {...styles.img}
                                                height="80"
                                                width="80"
                                                src={category.imageUrl ?? ""}
                                                altText={category?.imageAltText}
                                            />
                                        </Clickable>
                                    </GridItem>
                                    <GridItem {...styles.linkGridItem}>
                                        <Link
                                            href={category?.url}
                                            {...styles.subCategoryNameLink}
                                            data-test-selector={`newProductLinkListLink_${category?.name}`}
                                        >
                                            <Typography {...styles.linkTypography}>{category?.name}</Typography>
                                        </Link>
                                    </GridItem>
                                </GridContainer>
                            </StyledWrapper>
                        </GridItem>
                        )
                        
                    ),
                )}
            {cacheIndex > -1 &&
                newProductState.requestCache[cacheIndex].newProductsLoaded &&
                newProductState.requestCache[cacheIndex].results.newProducts.map((product: NewProductModel, index) => (
                    <GridItem key={`gridItem_${index}`} {...styles.subCategoryNameLinkItem}>
                        <StyledWrapper {...styles.linkWrapper}>
                            <GridContainer {...styles.linkContainer}>
                                <GridItem {...styles.imageGridItem}>
                                    <Clickable {...styles.imageClickable} href={product?.url}>
                                        <Img
                                            {...styles.img}
                                            height="80"
                                            width="80"
                                            src={product?.imageUrl}
                                            altText={product?.imageAltText}
                                        />
                                    </Clickable>
                                </GridItem>
                                <GridItem {...styles.linkGridItem}>
                                    <Link
                                        href={product?.url}
                                        {...styles.subCategoryNameLink}
                                        data-test-selector={`newProductLinkListLink_${product?.name}`}
                                    >
                                        <Typography {...styles.linkTypography}>{product?.name}</Typography>
                                    </Link>
                                </GridItem>
                            </GridContainer>
                        </StyledWrapper>
                    </GridItem>
                ))}
            <GridItem {...styles.footerGridItem}>
                <Link {...styles.footerLink} href={newProductsUrl}>
                    <Typography {...styles.footer}>{showMoreTitle}</Typography>
                </Link>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NewProductLinkList),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "NEW PRODUCTS",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.showMoreTitle,
                displayName: "Show More Button Text",
                editorTemplate: "TextField",
                defaultValue: "View More",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.itemsToReturn,
                displayName: "Number of Items to Return",
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "General",
                min: 1,
                max: 6,
                sortOrder: 3,
            },
            {
                name: fields.showType,
                displayName: "Show Products or Categories",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Categories",
                        value: "categories",
                    },
                    {
                        displayName: "Products",
                        value: "products",
                    },
                ],
                tooltip: "Determines whether to display New Products or New Product Categories in the Widget",
                defaultValue: "categories",
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showAsCategory,
                displayName: "Show as Category",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.newProductsAttributeId,
                displayName: "New Products Attribute Id",
                editorTemplate: "TextField",
                defaultValue: "c69e5006-e257-4494-a379-b18800e87b6e",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
